import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const GENERIC_FAILURE_MESSAGE = "There was an error when converting this organization to a business organization."

export default class extends Controller {
  static get targets() { return ["form", "saveButton", "organizationName", "organizationSlug"]; }

  syncSaveButton() {
    if (this.organizationNameTarget.value && this.organizationSlugTarget.value) {
      this.saveButtonTarget.removeAttribute("disabled");
    } else {
      this.saveButtonTarget.setAttribute("disabled", "disabled");
    }
  }

  save(event) {
    event.preventDefault();

    this.hideAllInlineErrorMessages();

    const options = RequestHelper.defaultPostOptions();
    // The browser carries these onto the redirect this POST triggers, and
    // Admin::OrganizationsController#show negotiates on both. Without them that silent hop renders
    // as full HTML and spends the one-shot flash notice before our own navigation can show it.
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*"
    });
    options.body = JSON.stringify({
      organization: {
        name: this.organizationNameTarget.value,
        slug: this.organizationSlugTarget.value
      }
    });

    fetch(this.formTarget.action, options).
      then(response => {
        // A successful convert redirects to the organization's show page, which fetch follows
        // itself, so response.url is where we actually landed.
        if (response.ok) {
          // S3: `response.url` is wherever fetch's redirect-following actually landed, so it is a
          // response-controlled value - asserted same-origin before navigating rather than trusted
          // outright. The failing branch does not navigate; it routes into this controller's own
          // existing error alert.
          if (RequestHelper.isSameOriginUrl(response.url)) {
            window.location.href = response.url;
          } else {
            this.displayErrorAlert(GENERIC_FAILURE_MESSAGE);
          }
          return;
        }

        return response.json().then(({ errors }) => this.displayFailure(errors));
      }).
      catch(this.logFailedCall);
  }

  displayFailure(errors) {
    this.displayAllInlineErrorMessages(errors);

    if (errors === undefined) {
      this.displayErrorAlert(GENERIC_FAILURE_MESSAGE);
      return;
    }

    if (errors.organization[0]) {
      this.displayErrorAlert(errors.organization);
    }
  }

  displayAllInlineErrorMessages(errors) {
    if (errors?.name) { this.appendErrorMessages(errors.name, this.organizationNameTarget); }
    if (errors?.slug) { this.appendErrorMessages(errors.slug, this.organizationSlugTarget); }
  }

  hideAllInlineErrorMessages() {
    this.removeErrorMessage(this.organizationNameTarget);
    this.removeErrorMessage(this.organizationSlugTarget);
  }

  appendErrorMessages(messages, target) {
    messages.forEach(message => {
      // Rails validation messages for name/slug. They are static strings today, but building the
      // node and setting textContent means a future validator that interpolates the submitted
      // value cannot turn this into an XSS sink. Rendered text is unchanged.
      const error = document.createElement("span");
      error.className = "control-label error";
      error.textContent = message;
      target.parentElement.insertAdjacentElement("beforeend", error);
    });
  }

  removeErrorMessage(target) {
    target.parentElement.querySelectorAll(".help-block").forEach(element => element.remove());
  }

  displayErrorAlert(message) {
    window.ltAlert({ message: message, type: "toast-error", duration: 2500, lasso: false });
  }

  logFailedCall(response) { console.warn(response); }
}
